/**
 * The inline layer of article prose.
 *
 * Article bodies are typed blocks rather than Markdown, but a paragraph still
 * needs a link inside a sentence, and a sentence about Thai still needs Thai in
 * it. This module is the whole of that: four inline markers, and one thing the
 * author never has to write.
 *
 * Runs of Thai script are found rather than tagged. A sentence written as
 * "the greeting สวัสดี opens a conversation" comes out with the Thai in its own
 * node, so the Thai typeface and `lang="th"` land on exactly the characters
 * that need them and an author never marks up their own examples.
 */

/** Thai, Unicode block 0E00 to 0E7F. Spaced runs stay one node. */
const THAI_RUN_SOURCE = "[\\u0E00-\\u0E7F]+(?:[ \\u00A0]+[\\u0E00-\\u0E7F]+)*";

function thaiRun(): RegExp {
  return new RegExp(THAI_RUN_SOURCE, "gu");
}

export type InlineNode =
  | { kind: "text"; value: string }
  | { kind: "thai"; value: string }
  | { kind: "strong"; children: InlineNode[] }
  | { kind: "em"; children: InlineNode[] }
  | { kind: "term"; children: InlineNode[] }
  | { kind: "link"; href: string; children: InlineNode[] };

/**
 * `[label](/path)`, `**strong**`, `_emphasis_`, and `` `term` ``. One pattern,
 * so the markers are matched in a single pass and cannot overlap.
 */
const INLINE_MARKUP_SOURCE =
  "\\[([^\\]]+)\\]\\(([^)\\s]+)\\)|\\*\\*([^*]+)\\*\\*|`([^`]+)`|_([^_]+)_";

/** Splits authored text into the nodes a renderer walks. */
export function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  const markup = new RegExp(INLINE_MARKUP_SOURCE, "g");
  let cursor = 0;

  for (
    let match = markup.exec(text);
    match !== null;
    match = markup.exec(text)
  ) {
    pushText(nodes, text.slice(cursor, match.index));
    cursor = match.index + match[0].length;

    const [, linkLabel, linkHref, strong, term, emphasis] = match;

    if (linkLabel !== undefined && linkHref !== undefined) {
      nodes.push({ kind: "link", href: linkHref, children: parseInline(linkLabel) });
    } else if (strong !== undefined) {
      nodes.push({ kind: "strong", children: parseInline(strong) });
    } else if (term !== undefined) {
      nodes.push({ kind: "term", children: parseInline(term) });
    } else if (emphasis !== undefined) {
      nodes.push({ kind: "em", children: parseInline(emphasis) });
    }
  }

  pushText(nodes, text.slice(cursor));
  return nodes;
}

/** Appends a plain run, splitting the Thai inside it into its own nodes. */
function pushText(nodes: InlineNode[], value: string): void {
  if (value === "") return;

  const thai = thaiRun();
  let cursor = 0;

  for (
    let match = thai.exec(value);
    match !== null;
    match = thai.exec(value)
  ) {
    if (match.index > cursor) {
      nodes.push({ kind: "text", value: value.slice(cursor, match.index) });
    }
    nodes.push({ kind: "thai", value: match[0] });
    cursor = match.index + match[0].length;
  }

  if (cursor < value.length) {
    nodes.push({ kind: "text", value: value.slice(cursor) });
  }
}

/** The reader-visible words, with every marker removed. */
export function inlineText(text: string): string {
  return flatten(parseInline(text));
}

function flatten(nodes: readonly InlineNode[]): string {
  return nodes
    .map((node) =>
      node.kind === "text" || node.kind === "thai" ? node.value : flatten(node.children),
    )
    .join("");
}

/** Every href the text links to, in the order they are written. */
export function inlineLinks(text: string): string[] {
  const links: string[] = [];
  collectLinks(parseInline(text), links);
  return links;
}

function collectLinks(nodes: readonly InlineNode[], into: string[]): void {
  for (const node of nodes) {
    if (node.kind === "text" || node.kind === "thai") continue;
    if (node.kind === "link") into.push(node.href);
    collectLinks(node.children, into);
  }
}

/**
 * A stable anchor for a heading, derived rather than authored so the heading,
 * its entry in the contents list, and the id it scrolls to cannot drift apart.
 *
 * Letters are kept whatever alphabet they are in. Stripping to `[a-z0-9]` was
 * fine while every heading on the site was English, and it collapses a page of
 * Thai questions into a page of identically-named anchors: every heading slugs
 * to nothing, every one falls back to "section", and a reader following a link
 * to the fourth question lands on the first. A fragment may hold any character
 * a URL may hold, and browsers have percent-encoded the rest for two decades.
 */
export function headingId(text: string): string {
  const slug = inlineText(text)
    .normalize("NFKD")
    // Latin combining accents only: the range stops well short of Thai.
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    // Marks are kept, because a Thai vowel or tone mark is part of the letter
    // rather than an accent on top of one, and dropping them spells nothing.
    .replace(/[^\p{Letter}\p{Number}\p{Mark}]+/gu, "-")
    .replace(/^-+|-+$/g, "");

  return slug === "" ? "section" : slug;
}

/** Counts words the way a reading time should: Thai is not written with spaces. */
export function countWords(text: string): number {
  const plain = inlineText(text);
  const latin = plain.replace(thaiRun(), " ").trim();
  const latinWords = latin === "" ? 0 : latin.split(/\s+/).length;
  const thaiCharacters = (plain.match(thaiRun()) ?? [])
    .join("")
    .replace(/\s+/g, "").length;

  // Three characters to a word is the usual rough measure for Thai, and a
  // reading time is a rough measure to begin with.
  return latinWords + Math.ceil(thaiCharacters / 3);
}
